import { useState } from 'react'
import { displayHeight, displayWeight } from '../../lib/units'
import type { UnitPreference } from '../../lib/units'
import { regenerateAppearance } from '../../lib/generateAppearance'

interface Props {
  character: {
    height_inches: number | null
    weight_lbs: number | null
    frame: string | null
    complexion: string | null
    hair_color: string | null
    eye_color: string | null
    comeliness: number | null
    physical_profile: {
      hairLength?: string
      marks?: string[]
      apparentAge?: string
      appearance?: string
      heightBand?: string
    } | null
    appearances: {
      travelling?: string
      at_work?: string
      leisure?: string
      formal?: string
      _source?: string
    } | null
  }
  pref: UnitPreference
  characterId?: string
  isGM?: boolean
  onRegenerate?: () => void
}

const CONTEXTS = [
  { key: 'travelling', label: 'Travelling' },
  { key: 'at_work', label: 'At Work' },
  { key: 'leisure', label: 'Leisure' },
  { key: 'formal', label: 'Formal' },
] as const

export function AppearancePanel({ character, pref, characterId, isGM, onRegenerate }: Props) {
  const [context, setContext] = useState<string>('travelling')
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [regenError, setRegenError] = useState<string | null>(null)
  const pp = character.physical_profile
  const appearances = character.appearances

  if (!pp && !appearances && !character.height_inches) {
    return null
  }

  const currentDesc = appearances?.[context as keyof typeof appearances]
  const source = appearances?._source

  async function handleRegenerate() {
    if (!characterId || isRegenerating) return
    setIsRegenerating(true)
    setRegenError(null)
    try {
      await regenerateAppearance(characterId)
      onRegenerate?.()
    } catch (err) {
      setRegenError(err instanceof Error ? err.message : 'Regeneration failed')
    } finally {
      setIsRegenerating(false)
    }
  }

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Physical Description</h3>
        <div className="flex items-center gap-2">
          {source && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
              source === 'claude' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {source === 'claude' ? 'AI' : 'template'}
            </span>
          )}
          {isGM && characterId && (
            <button
              onClick={handleRegenerate}
              disabled={isRegenerating}
              className="text-xs px-2 py-0.5 rounded border border-parchment-dark hover:bg-parchment-dark disabled:opacity-50 disabled:cursor-wait"
              title="Regenerate appearance descriptions using current equipment and injuries"
            >
              {isRegenerating ? 'Generating...' : 'Regenerate'}
            </button>
          )}
        </div>
      </div>

      {regenError && (
        <p className="text-xs text-red-600 mb-2">{regenError}</p>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-1 text-sm mb-3">
        {character.height_inches && (
          <div><span className="font-semibold">Height:</span> {displayHeight(character.height_inches, pref)}
            {pp?.heightBand && <span className="text-ink-light text-xs ml-1">({pp.heightBand})</span>}
          </div>
        )}
        {character.weight_lbs && (
          <div><span className="font-semibold">Weight:</span> {displayWeight(character.weight_lbs, pref)}</div>
        )}
        {character.frame && <div><span className="font-semibold">Frame:</span> {character.frame}</div>}
        {character.complexion && <div><span className="font-semibold">Complexion:</span> {character.complexion}</div>}
        {character.hair_color && (
          <div><span className="font-semibold">Hair:</span> {character.hair_color}
            {pp?.hairLength && <span className="text-ink-light text-xs ml-1">({pp.hairLength})</span>}
          </div>
        )}
        {character.eye_color && <div><span className="font-semibold">Eyes:</span> {character.eye_color}</div>}
        {character.comeliness && <div><span className="font-semibold">Comeliness:</span> {character.comeliness}</div>}
        {pp?.appearance && <div><span className="font-semibold">Appearance:</span> {pp.appearance}</div>}
        {pp?.apparentAge && <div className="col-span-2 text-ink-light text-xs italic">{pp.apparentAge}</div>}
      </div>

      {/* Marks/Scars */}
      {pp?.marks && pp.marks.length > 0 && (
        <div className="text-sm mb-3">
          <span className="font-semibold">Distinguishing marks:</span>
          <ul className="list-disc list-inside text-xs text-ink-light mt-0.5">
            {pp.marks.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* Contextual descriptions */}
      {appearances && Object.keys(appearances).filter(k => !k.startsWith('_')).length > 0 && (
        <div>
          <div className="flex gap-1 mb-2">
            {CONTEXTS.map(c => {
              const desc = appearances[c.key as keyof typeof appearances]
              if (!desc) return null
              return (
                <button key={c.key} onClick={() => setContext(c.key)}
                  className={`px-2 py-0.5 text-xs rounded ${
                    context === c.key ? 'bg-accent text-white font-bold' : 'border border-parchment-dark hover:bg-parchment-dark'
                  }`}>
                  {c.label}
                </button>
              )
            })}
          </div>
          {currentDesc && (
            <p className="text-sm text-ink-light italic leading-relaxed">{currentDesc}</p>
          )}
        </div>
      )}
    </div>
  )
}
